export function attachCustomerFinds(section) {
  const links = [...section.querySelectorAll('[data-customer-photo]')];
  const dialog = section.querySelector('[data-customer-dialog]');
  const image = dialog?.querySelector('[data-customer-dialog-image]');
  if (!links.length || !dialog || !image || typeof dialog.showModal !== 'function') return () => {};
  const closeButton = dialog.querySelector('[data-customer-close]');
  const previous = dialog.querySelector('[data-customer-previous]');
  const next = dialog.querySelector('[data-customer-next]');
  const original = dialog.querySelector('[data-customer-original]');
  const position = dialog.querySelector('[data-customer-position]');
  const caption = dialog.querySelector('[data-customer-dialog-caption]');
  const doc = section.ownerDocument;
  const listeners = [];
  let activeIndex = 0, lastFocus = null, previousOverflow = '', ownsScrollLock = false;
  const on = (target, type, handler) => { if (!target) return; target.addEventListener(type, handler); listeners.push(() => target.removeEventListener(type, handler)); };
  const showPhoto = index => {
    activeIndex = (index + links.length) % links.length;
    const link = links[activeIndex];
    image.src = link.href;
    image.alt = link.dataset.customerPhotoAlt || link.querySelector('img')?.alt || '';
    if (original) original.href = link.href;
    if (position) position.textContent = `${position.dataset.photoLabel || 'Photo'} ${activeIndex + 1} / ${links.length}`;
    if (caption) { caption.textContent = link.dataset.customerCaption || ''; caption.hidden = !caption.textContent; }
    if (previous) previous.disabled = links.length < 2;
    if (next) next.disabled = links.length < 2;
  };
  const release = () => {
    if (dialog.open) return;
    image.removeAttribute('src');
    image.alt = '';
    original?.removeAttribute('href');
    if (caption) { caption.textContent = ''; caption.hidden = true; }
    if (ownsScrollLock) {
      doc.body.style.overflow = doc.querySelector('dialog[open]') ? 'hidden' : previousOverflow;
      ownsScrollLock = false;
    }
    if (lastFocus?.isConnected && !doc.querySelector('dialog[open]')) lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  };
  on(section, 'click', event => {
    const link = event.target?.closest?.('[data-customer-photo]');
    let linkIndex = links.indexOf(link);
    if (linkIndex < 0 && link?.closest?.('[data-customer-clone]')) {
      const sourceIndex = Number(link.dataset.customerCloneIndex);
      if (Number.isInteger(sourceIndex) && sourceIndex >= 0 && sourceIndex < links.length) linkIndex = sourceIndex;
    }
    if (!link || linkIndex < 0 || event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (dialog.open || doc.querySelector('dialog[open]')) return;
    lastFocus = links[linkIndex];
    previousOverflow = doc.body.style.overflow;
    showPhoto(linkIndex);
    try { dialog.showModal(); }
    catch { lastFocus = null; release(); return; }
    event.preventDefault();
    doc.body.style.overflow = 'hidden';
    ownsScrollLock = true;
    closeButton?.focus({ preventScroll: true });
  });
  on(closeButton, 'click', () => dialog.close());
  on(previous, 'click', () => showPhoto(activeIndex - 1));
  on(next, 'click', () => showPhoto(activeIndex + 1));
  on(dialog, 'keydown', event => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.target?.closest?.('input,textarea,select,[contenteditable="true"]')) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); showPhoto(activeIndex + (event.key === 'ArrowLeft' ? -1 : 1)); }
  });
  on(dialog, 'cancel', event => { event.preventDefault(); dialog.close(); });
  on(dialog, 'click', event => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
  });
  on(dialog, 'close', release);
  const stopCarousel = attachCustomerCarousel(section, links);
  return () => {
    stopCarousel();
    if (dialog.open) dialog.close();
    listeners.splice(0).forEach(remove => remove());
    // close events are queued by browsers, so release synchronously on removal.
    release();
  };
}

export function attachCustomerCarousel(section, sourceLinks = [...section.querySelectorAll('[data-customer-photo]')]) {
  if (!section.hasAttribute?.('data-customer-auto') || sourceLinks.length < 2) return () => {};
  const track = section.querySelector('[data-customer-track]');
  const viewport = section.querySelector('[data-customer-viewport]');
  const doc = section.ownerDocument, win = doc?.defaultView;
  const cards = sourceLinks.map(link => link.closest('.customer-find'));
  if (!track || !viewport || !win || cards.some(card => !card?.cloneNode)) return () => {};
  const motion = win.matchMedia?.('(prefers-reduced-motion: reduce)') || { matches: true };
  const clones = [], listeners = [];
  let timer = null, loopTimer = null, frame = null, index = 0;
  let stopped = false;
  let inView = typeof win.IntersectionObserver !== 'function';
  const on = (target, name, listener) => { target?.addEventListener?.(name, listener); listeners.push(() => target?.removeEventListener?.(name, listener)); };
  // Enough visual repetitions for five desktop slots plus one incoming photo.
  // Only the original links remain in the accessibility tree and tab order.
  for (let repeatIndex = 0; repeatIndex < 6; repeatIndex++) {
    const sourceIndex = repeatIndex % cards.length;
    const clone = cards[sourceIndex].cloneNode(true);
    clone.setAttribute('data-customer-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    for (const node of [clone, ...clone.querySelectorAll('[id]')]) node.removeAttribute('id');
    for (const attribute of [...(clone.attributes || [])]) if (attribute.name.startsWith('data-shopify-')) clone.removeAttribute(attribute.name);
    for (const link of clone.querySelectorAll('[data-customer-photo]')) {
      link.dataset.customerCloneIndex = String(sourceIndex);
      link.setAttribute('tabindex', '-1');
    }
    for (const image of clone.querySelectorAll('img')) image.alt = '';
    track.append(clone); clones.push(clone);
  }
  const clearTimer = () => { if (timer !== null) win.clearTimeout(timer); timer = null; };
  const offset = (nextIndex, animate = false) => {
    if (!animate && loopTimer !== null) { win.clearTimeout(loopTimer); loopTimer = null; }
    index = nextIndex;
    if (frame !== null) win.cancelAnimationFrame?.(frame);
    const gap = parseFloat(win.getComputedStyle(track).columnGap || win.getComputedStyle(track).gap) || 0;
    const step = cards[0].getBoundingClientRect().width + gap;
    track.style.transition = animate ? '' : 'none';
    track.style.transform = `translate3d(${-index * step}px,0,0)`;
    track.dataset.customerSlide = String(index % cards.length);
    if (!animate) frame = win.requestAnimationFrame(() => { frame = null; if (!stopped) track.style.transition = ''; });
  };
  const canMove = () => {
    const bounds = viewport.getBoundingClientRect();
    return !stopped && !doc.hidden && inView && !motion.matches && bounds.width > 0 && bounds.bottom > 0 && bounds.top < win.innerHeight;
  };
  const finishLoop = () => {
    if (loopTimer === null || index < cards.length) return;
    win.clearTimeout(loopTimer);
    loopTimer = null;
    offset(0);
  };
  const schedule = () => {
    clearTimer();
    if (!canMove()) return;
    timer = win.setTimeout(() => {
      timer = null;
      if (!canMove()) return;
      offset(index + 1, true);
      // The CSS transition lasts 900ms. Reset only once the visual clone is
      // in place; the fallback also covers interrupted/missing browser events.
      if (index >= cards.length) loopTimer = win.setTimeout(finishLoop, 1000);
      schedule();
    }, 2000);
  };
  const syncMotion = () => {
    if (motion.matches) section.setAttribute('data-customer-auto-reduced', '');
    else section.removeAttribute('data-customer-auto-reduced');
    offset(0);
    schedule();
  };
  on(track, 'transitionend', event => { if (event.target === track && event.propertyName === 'transform') finishLoop(); });
  on(doc, 'visibilitychange', schedule);
  on(win, 'resize', () => { offset(0); schedule(); });
  on(motion, 'change', syncMotion);
  const intersection = typeof win.IntersectionObserver === 'function' ? new win.IntersectionObserver(entries => {
    inView = entries.some(entry => entry.target === viewport && entry.isIntersecting && entry.intersectionRatio > 0);
    schedule();
  }, { threshold: [0, 0.1] }) : null;
  intersection?.observe(viewport);
  syncMotion();
  return () => {
    stopped = true;
    clearTimer();
    if (loopTimer !== null) win.clearTimeout(loopTimer);
    if (frame !== null) win.cancelAnimationFrame?.(frame);
    intersection?.disconnect();
    listeners.splice(0).forEach(remove => remove());
    clones.forEach(clone => clone.remove());
    track.style.transition = ''; track.style.transform = '';
    delete track.dataset.customerSlide;
    section.removeAttribute('data-customer-auto-reduced');
  };
}

const mounted = new Map();
export function initCustomerFinds(root) {
  if (!root?.querySelectorAll) return;
  const sections = [...root.querySelectorAll('[data-customer-finds]')];
  if (root.matches?.('[data-customer-finds]')) sections.unshift(root);
  for (const section of sections) if (!mounted.has(section)) mounted.set(section, attachCustomerFinds(section));
}
export function unloadCustomerFinds(root) {
  for (const [section, destroy] of mounted) {
    if (section === root || root?.contains?.(section)) { destroy(); mounted.delete(section); }
  }
}
if (typeof document !== 'undefined') {
  initCustomerFinds(document);
  document.addEventListener('shopify:section:load', event => initCustomerFinds(event.target));
  document.addEventListener('shopify:section:unload', event => unloadCustomerFinds(event.target));
}
